import { Button, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useUserContext } from 'common/contexts/UserContext';
import { Input } from 'components/SignInput';
import { RequestJoinCompany } from 'pages/JoinCompany/RequestJoinCompany';

import * as React from 'react';
import { DefaultValues, useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  AvailableCompany,
  checkIfAutoJoinAvailable,
  checkScacFree,
  CreateCompanyPayload,
} from 'services/clients/user';
import { useCreateCompanyState } from '../utility';
import { Joinability } from './Joinability';
import { companyTypes } from './types';

export interface CreateFormValues {
  type: CreateCompanyPayload['company_type_detail'];
  name: string;
  scac?: string;
  otherType?: string;
  autoJoinEmailDomain: boolean;
}

interface Props {
  submit: (values: CreateFormValues) => void;
}

export const CompanyTypeSelector: React.FC<Props> = (props) => {
  const [{ stage, ...rest }] = useCreateCompanyState();
  const defaultValues: DefaultValues<CreateFormValues> = { autoJoinEmailDomain: true, ...rest };

  const { user } = useUserContext();

  const autoJoinAvilable = useQuery(
    ['checkIsAutoJoinAvailable', user?.email],
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    ({ signal }) => checkIfAutoJoinAvailable(user!.email, signal),
    {
      enabled: !!user,
      // eslint-disable-next-line no-console
      onError: (e) => console.error(e),
      refetchInterval: false,
      refetchOnWindowFocus: false,
    }
  );

  const { register, setValue, watch, formState, handleSubmit } = useForm<CreateFormValues>({
    mode: 'all',
    defaultValues,
  });

  const checkScac = useMutation(checkScacFree);
  const [company, setCompany] = React.useState<AvailableCompany | null>(null);

  register('type', { required: true });

  const companyTypeKey = watch('type');
  const companyType = companyTypes.find((t) => t.key === companyTypeKey);

  // If a user changes to a non-scac type remove the company object
  React.useEffect(() => {
    if (!companyType?.fields.includes('scac')) {
      setCompany(null);
    }
  }, [companyType]);

  const onSubmit = handleSubmit((values) => {
    let data: CreateFormValues = { ...values };

    if (autoJoinAvilable.data === 'auto_join_unavailable') {
      data = { ...data, autoJoinEmailDomain: false };
    }

    if (values.type !== 'Other') {
      data = { ...data, otherType: undefined };
    }

    if (!companyTypes.find((c) => c.key === values.type)?.fields.includes('scac')) {
      data = { ...data, scac: undefined };
    }

    props.submit(data);
  });

  return (
    <div>
      <form className="space-y-4" onSubmit={onSubmit}>
        <ToggleButtonGroup
          exclusive
          className="grid grid-cols-1 sm:grid-cols-2 gap-2"
          onChange={(_, value) =>
            setValue('type', value, { shouldDirty: true, shouldTouch: true, shouldValidate: true })
          }
          value={companyTypeKey}
        >
          {companyTypes.map((type) => (
            <ToggleButton key={type.key} value={type.key} className="sm:h-16">
              <h4>{type.label}</h4>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        {companyType?.fields.includes('name') && (
          <Input
            label="Enter company name*"
            placeholder="Company name"
            type="text"
            inputProps={register('name', {
              required: true,
              validate: {
                req: (v) => (!v ? 'This field is required.' : true),
              },
            })}
            error={formState.errors.name}
          />
        )}
        {companyType?.fields.includes('other') && (
          <Input
            label="Enter company type*"
            placeholder="Company type"
            type="text"
            inputProps={register('otherType', {
              required: true,
              validate: {
                req: (v) => (!v ? 'This field is required.' : true),
              },
            })}
            error={formState.errors.otherType}
          />
        )}
        {companyType?.fields.includes('scac') && (
          <Input
            label="Enter company SCAC*"
            placeholder="SCAC"
            type="text"
            inputProps={register('scac', {
              required: true,
              validate: {
                req: (v) => (!v ? 'This field is required.' : true),
                min: (v) => (v && v.length < 2 ? 'Must be at least 2 characters.' : true),
                max: (v) => (v && v.length > 4 ? 'Must be less than 5 characters.' : true),
                check: async (v) => {
                  if (!v) {
                    return true;
                  }

                  try {
                    const response = await checkScac.mutateAsync(v);
                    if (response.code === 'scac_exists') {
                      setCompany({ id: v, name: response.name });
                      return 'This SCAC is already in use. Join your company or submit another SCAC.';
                    }
                  } catch (e) {
                    // eslint-disable-next-line
                    console.error(e);
                    return 'Could not check SCAC.';
                  }
                  setCompany(null);
                  return true;
                },
              },
            })}
            error={formState.errors.scac}
          />
        )}
        {company && <RequestJoinCompany company={company} />}
        {companyType && (
          <>
            {autoJoinAvilable.data === 'auto_join_available' && !company && (
              <Joinability
                inputProps={register('autoJoinEmailDomain', { required: false })}
                defaultChecked={defaultValues.autoJoinEmailDomain}
              />
            )}
            {autoJoinAvilable.isError && (
              <div className="text-red-900">Could not check email for auto join toggle.</div>
            )}
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={!formState.isValid || autoJoinAvilable.isLoading}
            >
              Continue
            </Button>
          </>
        )}
      </form>
    </div>
  );
};
